import { getPlatformName } from '@/components/creator-discovery/utils';
import { Box, Button, Text } from '@chakra-ui/react';
import {
  MentionPlatform,
  MentionTrackingStatus,
  type MentionHeadlineMetric,
  type MentionIntelligenceResponse,
} from '@fluenceur/types';
import { useTranslation } from 'react-i18next';
import { MentionPerformanceTimeline } from './MentionPerformanceTimeline';
import { OpportunityInsightsPanel } from './OpportunityInsightsPanel';

interface MentionTrackingSectionProps {
  intelligence?: MentionIntelligenceResponse;
  creatorMatchScore: number | null;
  isLoading: boolean;
  isError: boolean;
  /** Slot rendered between the timeline and the opportunity panel (boost entrypoint). */
  boostPanel?: React.ReactNode;
  /** Mention platform, drives the natural metric unit and the reconnect copy. */
  platform?: MentionPlatform;
  onReconnect?: () => void;
  /**
   * A launched boost reorders the pane: the boost summary leads, the organic
   * timeline is demoted below it, and the pre-boost opportunity signal is hidden
   * (it belongs on the decision pane, not on a mention that is already boosted).
   */
  isBoosted?: boolean;
  /** Launch timestamp of the boost; marks "from here, we boosted" on the timeline. */
  boostLaunchedAt?: string | null;
}

/** What the section actually renders, decoupled from the raw tracking status. */
type TrackingView = 'story' | 'unavailable' | 'disconnected' | 'history';

export function MentionTrackingSection({
  intelligence,
  creatorMatchScore,
  isLoading,
  isError,
  boostPanel,
  platform = MentionPlatform.INSTAGRAM,
  onReconnect,
  isBoosted = false,
  boostLaunchedAt = null,
}: MentionTrackingSectionProps) {
  const points = intelligence?.timeline.points ?? [];
  const view = resolveView(isLoading, intelligence, points.length);
  const platformName = getPlatformName(platform);

  if (view === 'story') {
    return (
      <Box>
        <TrackingNotice messageKey="mentions.intelligence.tracking.unsupported_story" />
        {boostPanel}
        {!isBoosted && (
          <OpportunityInsightsPanel
            creatorMatchScore={creatorMatchScore}
            showTrend={false}
            isIntelligenceError={isError}
          />
        )}
      </Box>
    );
  }

  if (view === 'unavailable') {
    return (
      <Box>
        <TrackingNotice messageKey="mentions.intelligence.tracking.unsupported_media" />
        {boostPanel}
        {!isBoosted && (
          <OpportunityInsightsPanel
            creatorMatchScore={creatorMatchScore}
            showTrend={false}
            isIntelligenceError={isError}
          />
        )}
      </Box>
    );
  }

  if (view === 'disconnected') {
    return (
      <Box>
        <TrackingNotice
          messageKey="mentions.intelligence.tracking.integration_disconnected"
          values={{ platform: platformName }}
        >
          <ReconnectAction platformName={platformName} onReconnect={onReconnect} />
        </TrackingNotice>
        {boostPanel}
        {!isBoosted && (
          <OpportunityInsightsPanel
            creatorMatchScore={creatorMatchScore}
            isIntelligenceError={isError}
          />
        )}
      </Box>
    );
  }

  const headlineMetric = intelligence?.timeline.headlineMetric ?? 'interactions';
  const velocityPerHour = intelligence?.timeline.velocityPerHour ?? null;
  // Below the TikTok view floor the backend nulls the velocity while still
  // returning the curve. Surface that honestly instead of a silent gap, but only
  // for the views headline on an active, populated curve, so Instagram's
  // intentional silent null and the stopped/collecting states stay untouched.
  const showInsufficientSignal =
    intelligence?.trackingStatus === MentionTrackingStatus.ACTIVE &&
    headlineMetric === 'views' &&
    velocityPerHour === null &&
    points.length >= 2;

  // One self-contained "organic evolution" section: the curve, its launch marker,
  // the velocity caption and any tracking notice. No divider above or below; the
  // section spacing alone separates it from the top stats and the paid block.
  const timelineBlock = (
    <Box py={5}>
      <MentionPerformanceTimeline
        points={points}
        headlineMetric={headlineMetric}
        isLoading={isLoading}
        isError={isError}
        launchedAt={isBoosted ? boostLaunchedAt : null}
      />
      <VelocityContext velocityPerHour={velocityPerHour} headlineMetric={headlineMetric} />
      {showInsufficientSignal && (
        <TimelineNotice messageKey="mentions.intelligence.tracking.insufficient_signal" />
      )}
      {intelligence?.trackingStatus === MentionTrackingStatus.COLLECTING && (
        <TimelineNotice messageKey="mentions.intelligence.tracking.collecting" />
      )}
      {intelligence?.trackingStatus === MentionTrackingStatus.TRACKING_STOPPED && (
        <TimelineNotice messageKey="mentions.intelligence.tracking.tracking_stopped" />
      )}
    </Box>
  );

  // The organic curve leads (it sets the context and marks where the boost kicked
  // in), then the paid block quantifies the lift. The pre-boost opportunity signal
  // is hidden once a boost exists; it belongs on the decision pane.
  return (
    <Box>
      {timelineBlock}
      {boostPanel}
      {!isBoosted && (
        <OpportunityInsightsPanel
          creatorMatchScore={creatorMatchScore}
          velocityTrend={intelligence?.opportunity.signals.velocityTrend ?? null}
          pointsCount={points.length}
          isLoading={isLoading}
          isIntelligenceError={isError}
        />
      )}
    </Box>
  );
}

/**
 * Maps the server-derived tracking status to a view. The "don't regress an
 * existing curve" rule lives in the backend, which never reports
 * `integration_disconnected` once a curve exists. The `pointCount < 2` here is
 * only a defensive guard against an inconsistent response, not the business rule.
 */
function resolveView(
  isLoading: boolean,
  intelligence: MentionIntelligenceResponse | undefined,
  pointCount: number
): TrackingView {
  if (isLoading || !intelligence) return 'history';
  if (intelligence.trackingStatus === MentionTrackingStatus.UNSUPPORTED_STORY) return 'story';
  // A never-trackable media only takes over while there is no curve to keep.
  if (intelligence.trackingStatus === MentionTrackingStatus.UNSUPPORTED_MEDIA && pointCount < 2) {
    return 'unavailable';
  }
  if (
    intelligence.trackingStatus === MentionTrackingStatus.INTEGRATION_DISCONNECTED &&
    pointCount < 2
  ) {
    return 'disconnected';
  }
  return 'history';
}

function TrackingNotice({
  messageKey,
  values,
  children,
}: {
  messageKey: string;
  values?: Record<string, string | number>;
  children?: React.ReactNode;
}) {
  const { t } = useTranslation();

  return (
    <Box
      py={5}
      borderBottom="1px solid"
      borderColor="gray.100"
      data-testid="mention-tracking-notice"
    >
      <Box bg="gray.50" rounded="md" px={4} py={4}>
        <Text fontSize="sm" color="gray.700" lineHeight="short">
          {t(messageKey, values)}
        </Text>
        {children}
      </Box>
    </Box>
  );
}

function ReconnectAction({
  platformName,
  onReconnect,
}: {
  platformName: string;
  onReconnect?: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Button mt={3} size="xs" variant="outline" colorPalette="gray" onClick={onReconnect}>
      {t('mentions.intelligence.tracking.reconnect', { platform: platformName })}
    </Button>
  );
}

/**
 * Terse caption under the timeline: the collecting hint, or the cause-neutral
 * stopped notice once tracking has ended (mediaDeletedAt). The curve stays
 * visible above it; the richer FOU-700 treatment builds on this.
 */
function TimelineNotice({ messageKey }: { messageKey: string }) {
  const { t } = useTranslation();

  return (
    <Box pt={4}>
      <Text fontSize="xs" color="gray.500" lineHeight="short">
        {t(messageKey)}
      </Text>
    </Box>
  );
}

function VelocityContext({
  velocityPerHour,
  headlineMetric,
}: {
  velocityPerHour: number | null;
  headlineMetric: MentionHeadlineMetric;
}) {
  const { t } = useTranslation();

  if (velocityPerHour === null) return null;

  const rounded = Math.round(velocityPerHour);
  const sign = rounded >= 0 ? '+' : '';

  return (
    <Text
      mt={2}
      fontSize="xs"
      color="gray.500"
      lineHeight="short"
      data-testid="mention-velocity-context"
    >
      {/* The unit follows the headline metric: views/hour on TikTok, interactions/hour on Instagram. */}
      {t(`mentions.intelligence.velocityPerHour.${headlineMetric}`, { value: `${sign}${rounded}` })}
    </Text>
  );
}
